import React, { useEffect, useRef } from "react";

/**
 * ProtectedCanvasImage
 *
 * Renders a thumbnail into <canvas> instead of a visible <img>.
 *
 * Notes:
 * - Uses Image() instead of fetch().blob() so it also works with
 *   CDN/proxy images that may not expose CORS headers.
 * - Right click and drag are blocked.
 * - For cross-origin images, leaving crossOrigin unset will usually
 *   taint the canvas, preventing canvas.toDataURL()/getImageData().
 * - The browser can still receive the preview pixels; this is not a
 *   replacement for protecting the original high-resolution asset.
 */
const ProtectedCanvasImage = ({
  src,
  alt = "",
  onClick,
  onLoad,
  onError,
  width = "100%",
  height = 220,
  objectFit = "cover",
  objectPosition = "center center",
  className = "",
  style = {},
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const drawImageToCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    const rect = canvas.getBoundingClientRect();

    const fallbackHeight =
      typeof height === "number"
        ? height
        : parseFloat(height) || 220;

    const displayWidth = Math.max(
      rect.width || canvas.clientWidth || 1,
      1
    );

    const displayHeight = Math.max(
      rect.height || canvas.clientHeight || fallbackHeight,
      1
    );

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      2
    );

    canvas.width = Math.round(
      displayWidth * pixelRatio
    );

    canvas.height = Math.round(
      displayHeight * pixelRatio
    );

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.setTransform(
      pixelRatio,
      0,
      0,
      pixelRatio,
      0,
      0
    );

    ctx.clearRect(
      0,
      0,
      displayWidth,
      displayHeight
    );

    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;

    if (!sourceWidth || !sourceHeight) {
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const position =
      String(objectPosition || "")
        .toLowerCase();

    const alignTop =
      position.includes("top");

    const alignBottom =
      position.includes("bottom");

    const alignLeft =
      position.includes("left");

    const alignRight =
      position.includes("right");

    if (objectFit === "contain") {
      const scale = Math.min(
        displayWidth / sourceWidth,
        displayHeight / sourceHeight
      );

      const drawWidth =
        sourceWidth * scale;

      const drawHeight =
        sourceHeight * scale;

      let dx =
        (displayWidth - drawWidth) / 2;

      let dy =
        (displayHeight - drawHeight) / 2;

      if (alignLeft) dx = 0;
      if (alignRight) dx = displayWidth - drawWidth;
      if (alignTop) dy = 0;
      if (alignBottom) dy = displayHeight - drawHeight;

      ctx.drawImage(
        image,
        dx,
        dy,
        drawWidth,
        drawHeight
      );

      return;
    }

    /*
     * object-fit: cover
     */
    const sourceAspect =
      sourceWidth / sourceHeight;

    const targetAspect =
      displayWidth / displayHeight;

    let sx = 0;
    let sy = 0;
    let sw = sourceWidth;
    let sh = sourceHeight;

    if (sourceAspect > targetAspect) {
      sw = sourceHeight * targetAspect;

      if (alignLeft) {
        sx = 0;
      } else if (alignRight) {
        sx = sourceWidth - sw;
      } else {
        sx = (sourceWidth - sw) / 2;
      }
    } else {
      sh = sourceWidth / targetAspect;

      if (alignTop) {
        sy = 0;
      } else if (alignBottom) {
        sy = sourceHeight - sh;
      } else {
        sy = (sourceHeight - sh) / 2;
      }
    }

    ctx.drawImage(
      image,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      displayWidth,
      displayHeight
    );
  };

  useEffect(() => {
    if (!src) {
      onError?.();
      return;
    }

    let cancelled = false;

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    const image = new Image();
    image.decoding = "async";

    image.onload = () => {
      if (cancelled) return;

      imageRef.current = image;

      requestAnimationFrame(() => {
        drawImageToCanvas();
        onLoad?.();
      });

      if (
        typeof ResizeObserver !== "undefined" &&
        canvasRef.current
      ) {
        resizeObserverRef.current =
          new ResizeObserver(() => {
            requestAnimationFrame(
              drawImageToCanvas
            );
          });

        resizeObserverRef.current.observe(
          canvasRef.current
        );
      }
    };

    image.onerror = () => {
      if (cancelled) return;

      console.error(
        "Protected thumbnail failed to load:",
        src
      );

      onError?.();
    };

    image.src = src;

    return () => {
      cancelled = true;

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      image.onload = null;
      image.onerror = null;
      imageRef.current = null;
    };
  }, [
    src,
    height,
    objectFit,
    objectPosition,
  ]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      className={className}
      onClick={onClick}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDragStart={(event) => {
        event.preventDefault();
      }}
      style={{
        display: "block",
        width,
        height:
          typeof height === "number"
            ? `${height}px`
            : height,
        cursor:
          onClick
            ? "pointer"
            : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        ...style,
      }}
    />
  );
};

export default ProtectedCanvasImage;